/* ⚠️ GENERATED from app/services/mediaEditor.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/mediaEditor.ts — the editor's catalogue, shared by both clients.
//
// 🟥 THE FILTER PALETTE WAS A LOCAL CONST INSIDE A 19,000-LINE SCREEN. Fourteen
//    names and fourteen hex colours declared in the middle of the phone's editor
//    component, with the viewer's renderer reading `post.filterColor` and
//    trusting whatever it found. That was fine while one client could produce a
//    filtered post. A second one cannot copy the list: two copies of fourteen
//    colours drift the first time either is tuned, and a post would then look
//    like one grade on the phone and a different one in the browser.
//
// 🟥 SAME FOR THE STICKER KINDS. `'emoji' | 'time' | 'date' | 'location' |
//    'poll' | 'slider'` was the declared type and the editor placed three more
//    the type never mentioned — `time_24`, `clock`, `gallery_image`. A renderer
//    written from the type alone silently drops three of the nine.
//
// 🟢 SO THE CATALOGUE IS DATA, IN ONE FILE, IMPORTED BY BOTH. The browser's
//    editor is generated from this list rather than transcribed from a
//    screenshot of the phone's.

/* ── filters ──────────────────────────────────────────────────────────────── */

/**
 * 🟢 A TINT, COMPOSITED WITH `overlay` — not a baked colour matrix. That is why
 *    a receiving client needs nothing but the stored colour and an opacity to
 *    reproduce the grade, on any platform, with no image processing at all.
 */

export const EDITOR_FILTERS = [{
  id: 'pure',
  color: 'transparent'
}, {
  id: 'fog',
  color: '#78909C'
}, {
  id: 'dusk',
  color: '#FF7043'
}, {
  id: 'cloudy',
  color: '#37474F'
}, {
  id: 'vibrant',
  color: '#FFCA28'
}, {
  id: 'sunrise',
  color: '#FFE082'
}, {
  id: 'solo',
  color: '#000000'
}, {
  id: 'twilight',
  color: '#7E57C2'
}, {
  id: 'HD',
  color: '#FFFFFF'
}, {
  id: 'crush',
  color: '#FF1744'
}, {
  id: 'vivid',
  color: '#1DE9B6'
}, {
  id: 'clean',
  color: '#E0F7FA'
}, {
  id: 'bright',
  color: '#FAFAFA'
}, {
  id: 'quality',
  color: '#212121'
}];

/** The stored colour for a filter id — `transparent` for anything unknown. */
export function filterColorOf(id) {
  for (const f of EDITOR_FILTERS) if (f.id === id) return f.color;
  return 'transparent';
}

/** True when a stored filterColor is a grade rather than nothing. */
export function isTinted(color) {
  return !!color && color !== 'transparent';
}

/* ── stickers ─────────────────────────────────────────────────────────────── */

/**
 * 🟥 NINE KINDS, NOT SIX. The phone's own `stickers?: { type?: … }` annotation
 *    listed six and its editor placed nine — the three it forgot are the ones a
 *    second renderer would silently drop.
 */
export const STICKER_KINDS = ['emoji', 'time', 'time_24', 'clock', 'date', 'location', 'gallery_image', 'poll', 'slider'];
/** The ones the "Recommended" rail offers, in the phone's order. */
export const WIDGET_KINDS = ['time', 'time_24', 'clock', 'date', 'location', 'gallery_image', 'poll', 'slider'];

/** Does this kind need somebody to answer it? */
export function isInteractiveKind(kind) {
  return kind === 'poll' || kind === 'slider';
}

/**
 * The text a freshly-placed clock/date widget shows.
 *
 * 🟥 STAMPED ONCE, AT PLACEMENT, AND NEVER RECOMPUTED. These are a photograph
 *    of a moment — "21:58" means the time the picture was taken, not the time
 *    you happen to be scrolling past it — which is also why the value is stored
 *    on the sticker rather than derived at render.
 * 🟢 `time_24` IS PINNED TO en-GB rather than the viewer's locale, because the
 *    widget's entire purpose is the 24-hour format; asking the runtime for "the
 *    local short time" gives a 12-hour string on half the devices that place it.
 */
export function widgetInitialText(kind, now) {
  const d = now || new Date();
  if (kind === 'time' || kind === 'clock') return d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });
  if (kind === 'time_24') return d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit'
  });
  if (kind === 'date') return d.toLocaleDateString();
  return '';
}
/**
 * A sticker as the editor first drops it.
 *
 * 🟢 `y: -100` IS THE PHONE'S DROP POINT, not a rounding of it. Stickers are
 *    anchored 40% down the picture; dropping one at dead centre would put it
 *    under the caption on a portrait clip, which is why the phone lifts it.
 */
export function newSticker(kind, id, now) {
  return {
    id,
    type: kind,
    x: 0,
    y: -100,
    scale: 1,
    question: '',
    options: ['Yes', 'No'],
    locationText: widgetInitialText(kind, now)
  };
}

/* ── the drawn-on caption ─────────────────────────────────────────────────── */

export const TEXT_MODES = ['shadow', 'box', 'highlight', 'outline', 'neon'];
export const TEXT_COLORS = ['#FFFFFF', '#000000', '#FF3B30', '#FF9500', '#FFCC00', '#34C759', '#00C7BE', '#0A84FF', '#5856D6', '#FF2D55'];

/** px, so a stored size means the same thing on both clients. */
export const TEXT_SIZES = {
  small: 16,
  med: 24,
  big: 36
};

/**
 * 🟥 A KEYWORD **OR** A NUMBER. Old posts carry 'small' | 'med' | 'big'; the
 *    editor has written raw pixels since it grew a slider. A renderer that
 *    handles only one of the two draws half the archive at the wrong size.
 */
export function fontPx(size) {
  if (typeof size === 'number' && Number.isFinite(size)) return size;
  const k = String(size || 'med');
  return TEXT_SIZES[k] || TEXT_SIZES.med;
}

/* ── trim ─────────────────────────────────────────────────────────────────── */

/**
 * The cut stored on a post, or null when the clip is whole.
 *
 * 🟥 THE PHONE BAKES ITS TRIM AND THE BROWSER CANNOT. Native trimming re-encodes
 *    the file; in a browser that means shipping a video encoder to every visitor
 *    to save two seconds off a clip. So the browser stores the RANGE and both
 *    clients honour it at playback — which is strictly better anyway, because a
 *    stored range is reversible and a re-encode is not.
 * 🟥 AND A ZERO-LENGTH OR BACKWARDS RANGE IS NO RANGE. A malformed pair would
 *    otherwise pause the video forever on its first frame.
 */
export function trimOf(post) {
  if (!post) return null;
  const start = Number(post.trimStartMs);
  const end = Number(post.trimEndMs);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  if (start < 0 || end <= start) return null;
  return {
    start,
    end
  };
}

/**
 * Where playback should be, given where it is.
 *
 * 🟢 RETURNS null WHEN NOTHING NEEDS DOING, so a caller can use it as its own
 *    "should I seek?" test rather than seeking on every timeupdate — which
 *    would stutter the clip it is trying to play.
 */
export function seekForTrim(positionMs, trim) {
  if (!trim) return null;
  const p = Number(positionMs);
  if (!Number.isFinite(p)) return null;
  /* a small lead-out: `ended` never fires on a trimmed clip, so the loop back
     to the start has to happen just before the cut point rather than at it */
  if (p >= trim.end - 60 || p < trim.start - 200) return trim.start;
  return null;
}
